/**
 * Frontend configuration for supported image generation models
 * This mirrors the backend configuration in supabase/functions/_shared/config/imageModels.ts
 */

export interface ImageModelConfig {
  id: string;
  name: string;
  description: string;
  provider: 'openai' | 'stability' | 'other';
  maxPromptLength: number;
  supportedSizes: string[];
  timeout: number;
  costPerImage?: number;
}

export const SUPPORTED_IMAGE_MODELS: Record<string, ImageModelConfig> = {
  'gpt-image-1': {
    id: 'gpt-image-1',
    name: 'GPT Image 1',
    description: 'State-of-the-art quality, excels at detailed and complex prompts',
    provider: 'openai',
    maxPromptLength: 4000,
    supportedSizes: ['1024x1024'],
    timeout: 120000, // 120 seconds
    costPerImage: 0.04
  },
  'dall-e-3': {
    id: 'dall-e-3',
    name: 'DALL-E 3',
    description: 'Previous-generation model faster outputs with solid everyday quality',
    provider: 'openai',
    maxPromptLength: 4000,
    supportedSizes: ['1792x1024', '1024x1792', '1024x1024'],
    timeout: 120000, // 120 seconds
    costPerImage: 0.08
  },
  'dall-e-2': {
    id: 'dall-e-2',
    name: 'DALL-E 2',
    description: 'Original model: simple image generation',
    provider: 'openai',
    maxPromptLength: 1000,
    supportedSizes: ['1024x1024', '512x512', '256x256'],
    timeout: 90000, // 90 seconds
    costPerImage: 0.02
  },
  'stable-diffusion': {
    id: 'stable-diffusion',
    name: 'Stable Diffusion XL',
    description: 'Stability AI\'s advanced image generation model',
    provider: 'stability',
    maxPromptLength: 4000,
    supportedSizes: ['1024x1024'],
    timeout: 90000, // 90 seconds
    costPerImage: 0.02
  }
};

export const DEFAULT_IMAGE_MODEL = 'gpt-image-1';

export function isValidImageModel(model: string): boolean {
  return model in SUPPORTED_IMAGE_MODELS;
}

export function getImageModelConfig(model: string): ImageModelConfig | null {
  return SUPPORTED_IMAGE_MODELS[model] || null;
}

export function getSupportedImageModels(): string[] {
  return Object.keys(SUPPORTED_IMAGE_MODELS);
}

export function getImageModelOptions() {
  return Object.values(SUPPORTED_IMAGE_MODELS).map(model => ({
    value: model.id,
    label: model.name,
    description: model.description
  }));
}
